import {
  ShowContextProvider,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  FunctionField,
  Labeled,
  useEditController,
} from "react-admin";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FaEye } from "react-icons/fa";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { DateOnlyField } from "../../components/DateOnlyField";
import { PaymentMethodChip } from "../../components/PaymentMethodChip";
import { colors, fontFamilyMono } from "../../data/tokens";
import { transactionCategoryColor } from "./transactions-util";

// Ficha da transação reorganizada: o VALOR é a informação principal
// (grande, colorido por tipo), com os badges de tipo/forma ao lado;
// abaixo, duas colunas — vínculos (sócio, órfão, fatura) e detalhes
// (data, competência, categoria…) — e todo campo vazio fica oculto,
// como no restante do painel. A versão antiga misturava rótulos
// errados ("ID" mostrando a competência), campos vazios sempre
// visíveis e o membro órfão em dobro.
export const TransactionsShow = (props: any) => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "transactions/crud",
    id,
  });

  return (
    <>
      <ShowContextProvider value={controller}>
        <SimpleShowLayout className="RaShowClass">
          <PageTopBreadcrumb
            title="Transações"
            firstPath="/#/transactions/crud"
            secondTitle="Visualizando"
            thirdTitle={controller && controller.record && controller.record.id}
            actionType="edit"
          />
          <Card sx={{ padding: 2, marginBottom: 3 }}>
            <CardContent>
              {/* Cabeçalho: valor em destaque + badges de tipo e forma */}
              <FunctionField
                render={(record: any) => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{ fontSize: 12, color: colors.textMuted }}
                      >
                        Transação {record.id}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 30,
                          fontWeight: 700,
                          fontFamily: fontFamilyMono,
                          lineHeight: 1.2,
                          color:
                            record.type === "EXPENSE"
                              ? colors.action
                              : colors.success,
                        }}
                      >
                        {Number(record.value ?? 0).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        size="small"
                        color={record.type === "EXPENSE" ? "error" : "success"}
                        label={
                          record.type === "EXPENSE" ? "Despesa" : "Receita"
                        }
                      />
                      <PaymentMethodChip method={record.paymentMethod} />
                    </Box>
                  </Box>
                )}
              />

              <Divider sx={{ marginY: 2 }} />

              <Grid container spacing={0.5}>
                {/* Coluna 1 — vínculos */}
                <Grid item xs={12} md={6}>
                  <Stack>
                    <FunctionField
                      render={(record: any) =>
                        record.memberId ? (
                          <Labeled>
                            <ReferenceField
                              source="memberId"
                              label="Sócio"
                              reference="users/crud"
                              link="show"
                            >
                              <TextField source="fullName" />
                            </ReferenceField>
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    {/* Órfão em UMA linha (antes o ID aparecia duas
                        vezes: como texto e como botão) */}
                    <FunctionField
                      render={(record: any) =>
                        record.orphanedMemberId ? (
                          <Labeled label="Membro órfão (sistema antigo)">
                            <FunctionField
                              render={() => (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<FaEye />}
                                  onClick={() => {
                                    window.location.href = `/#/orphaned-users/crud/${record.orphanedMemberId}/show`;
                                  }}
                                >
                                  {record.orphanedMemberId}
                                </Button>
                              )}
                            />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.iuguId ? (
                          <Labeled label="Fatura na Iugu">
                            <FunctionField
                              render={() => (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<FaEye />}
                                  sx={{ fontFamily: fontFamilyMono }}
                                  onClick={() => {
                                    window.location.href = `/#/iugu-invoices?iugu_id=${record.iuguId}`;
                                  }}
                                >
                                  {record.iuguId}
                                </Button>
                              )}
                            />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.cpf ? (
                          <Labeled label="CPF gravado na transação">
                            <TextField
                              source="cpf"
                              sx={{ fontFamily: fontFamilyMono }}
                            />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.enrollment ? (
                          <Labeled label="Matrícula gravada na transação">
                            <TextField
                              source="enrollment"
                              sx={{ fontFamily: fontFamilyMono }}
                            />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                  </Stack>
                </Grid>

                {/* Coluna 2 — detalhes da cobrança */}
                <Grid item xs={12} md={6}>
                  <Stack>
                    <Labeled>
                      <DateOnlyField
                        source="referenceDate"
                        label="Data Referência"
                      />
                    </Labeled>
                    <FunctionField
                      render={(record: any) =>
                        record.sCompetencia ? (
                          <Labeled label="Competência">
                            {/* A versão antiga rotulava este campo de "ID" */}
                            <TextField
                              source="sCompetencia"
                              sx={{ fontFamily: fontFamilyMono }}
                            />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.id_Receita ? (
                          <Labeled label="Categoria">
                            <ReferenceField
                              source="id_Receita"
                              reference="transaction-categories/crud"
                              link="show"
                            >
                              <FunctionField
                                render={(cat: any) =>
                                  cat?.name ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={transactionCategoryColor(cat.name)}
                                      label={cat.name}
                                    />
                                  ) : null
                                }
                              />
                            </ReferenceField>
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.receipt ? (
                          <Labeled label="Nota fiscal">
                            <TextField source="receipt" />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <FunctionField
                      render={(record: any) =>
                        record.cheque ? (
                          <Labeled label="Cheque">
                            <TextField source="cheque" />
                          </Labeled>
                        ) : (
                          <></>
                        )
                      }
                    />
                  </Stack>
                </Grid>

                {/* Observações em largura total, só quando existirem */}
                <FunctionField
                  render={(record: any) =>
                    record.memo ? (
                      <Grid item xs={12}>
                        <Labeled label="Observações">
                          <TextField source="memo" />
                        </Labeled>
                      </Grid>
                    ) : (
                      <></>
                    )
                  }
                />
              </Grid>
            </CardContent>
          </Card>
        </SimpleShowLayout>
      </ShowContextProvider>
    </>
  );
};
